import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import { withStyles } from '@material-ui/core/styles';
import classNames from 'classnames';
import relationships from 'Consts/Relationships';
import Moment from 'moment';
import { useLocation } from 'react-router-dom';
import { pickImplementation } from 'Helpers/Affordances';
import { fullDate } from 'Consts/DateFormats';
import useUserContext from 'Context/UserContext';
import TaskCard from 'Components/AdvisorApp/Viewport/TodayDashboard/Tasks/TaskCard';
import { QueryVal, removeQuery } from 'Helpers/Query';
import { Link } from 'Components/UI/FacetRouter/FacetRouter';
import { taskBoardUri } from 'Consts/advisorUris';
import useSimpleHousehold from 'Hooks/useSimpleHousehold';
import useCollection from 'Hooks/useCollection';
import TodayDashboardTasksOverride from 'Resources/TodayDashboard';
import { useRefreshActionItem } from 'Hooks/useRefreshEvent';
import SkeletonCard from 'Components/AdvisorApp/Viewport/TodayDashboard/Tasks/SkeletonCard';
import _ from 'lodash';
import withEventTrackingLogEvent from 'EventTracking/withEventTrackingLogEvent';
import frRelationships from 'facet-rest/src/consts/relationships';
import { Switch } from '@material-ui/core';
import { getStartOfDayLocalFromUTC } from 'Helpers/DateTime';
import LayoutItem from '../LayoutItem';

const styles = theme => ({
  root: {
    height: '100%',
    minHeight: '500px',
  },
  addIcon: {
    height: '50%',
    color: theme.palette.facet.accentGreen,
  },
  taskTitle: {
    fontWeight: theme.typography.fontWeightBold,
  },
  upcomingTitle: {
    color: theme.palette.facet.primaryBlue,
  },
  overdueTitle: {
    color: theme.palette.facet.red,
  },
  taskContainer: {
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'space-between',
    height: '100%',
  },
  tasks: {
    display: 'flex',
    flexDirection: 'column',
    flex: 1,
    paddingTop: theme.spacing(1),
  },
  taskColumn: {
    flex: 1,
    margin: theme.spacing(2),
  },
  seeAllText: {
    marginTop: theme.spacing(1),
    marginBottom: theme.spacing(1),
    marginLeft: theme.spacing(2),
  },
});

const Tasks = ({ classes, className, EventTracking }) => {
  const { userResource } = useUserContext();
  const { collection, setCollection } = useCollection();
  const { householdResource } = useSimpleHousehold();
  const { logEvent, rootNodeProps } = EventTracking;
  const location = useLocation();
  const [displayOtherTasks, setDisplayOtherTasks] = useState(false);
  const [loading, setLoading] = useState(false);
  const taskAffordance = displayOtherTasks
    ? frRelationships.otherTodayDashboardTasks.name
    : frRelationships.agentTodayDashboardTasks.name;
  const toggleDisplayOtherTasks = () => {
    setDisplayOtherTasks((current) => {
      logEvent('Toggled Display Other Tasks', {
        displayOtherTasks: !current,
      });
      return !current;
    });
  };

  const fetchActionItems = async () => {
    const actionItems = await userResource
      ?.relationship(taskAffordance)
      ?.link('related')
      .Get({ metaOverrideSet: TodayDashboardTasksOverride });

    if (actionItems) {
      setCollection(actionItems);
    }
  };

  useRefreshActionItem(() => {
    fetchActionItems();
  }, [userResource, collection]);

  useEffect(() => {
    setLoading(true);
    fetchActionItems().then(() => setLoading(false));
  }, [userResource, displayOtherTasks]);

  useEffect(() => {
    if (QueryVal('refresh') === 'actionItem') {
      removeQuery('refresh');
      fetchActionItems();
    }
  }, [location.search]);

  const taskboardUriParams = pickImplementation(userResource, {
    [relationships.default]: {
      householdId: householdResource?.id(),
      link: householdResource
        ?.relationship(relationships.simpleActionItems)
        .link('related')
        .url(),
    },
  });

  const today = Moment().startOf('day');
  const { thisWeeksTasks, overdueTasks } = collection
    ?.resources()
    .list()
    .reduce(
      (acc, resource) => {
        const dueDateMoment = new Moment(resource.attr('dueDate'));
        if (dueDateMoment.isBefore(today)) {
          acc.overdueTasks.push(resource);
        } else {
          acc.thisWeeksTasks.push(resource);
        }
        return acc;
      },
      { thisWeeksTasks: [], overdueTasks: [] },
    ) || { thisWeeksTasks: [], overdueTasks: [] };

  const TaskContent = () => (loading ? (
    <>
      {_.range(4).map(value => (
        <SkeletonCard key={value} />
      ))}
    </>
  ) : (
    <>
      {thisWeeksTasks.map(actionItem => (
        <TaskCard
          key={actionItem.id()}
          {...actionItem.attributes()}
          owner={actionItem.attr('ownerName')}
          client={actionItem.attr('clientName')}
          dueDate={actionItem.attr('dueDate') ? Moment(getStartOfDayLocalFromUTC(actionItem.attr('dueDate'))).format(fullDate) : null}
          resource={actionItem}
        />
      ))}
      {overdueTasks.map(actionItem => (
        <TaskCard
          overdue
          key={actionItem.id()}
          {...actionItem.attributes()}
          owner={actionItem.attr('ownerName')}
          client={actionItem.attr('clientName')}
          dueDate={actionItem.attr('dueDate') ? Moment(getStartOfDayLocalFromUTC(actionItem.attr('dueDate'))).format(fullDate) : null}
          resource={actionItem}
        />
      ))}
    </>
  ));

  return (
    <div className={classNames(classes.root, className)} {...rootNodeProps}>
      <LayoutItem
        title="Tasks"
        variant="blue"
        onChevronClick={() => {}}
        HeaderComponent={
          <div>
            Display Other Tasks
            <Switch
              checked={displayOtherTasks}
              onChange={toggleDisplayOtherTasks}
            />
          </div>
        }
        FooterComponent={
          <Link
            onClick={() => logEvent('Clicked See All link')}
            to={taskBoardUri(taskboardUriParams)}
            test="pd"
          >
            See All ({collection?.resources()?.length() || 0})
          </Link>
        }
      >
        <div className={classes.tasks}>
          <TaskContent />
        </div>
      </LayoutItem>
    </div>
  );
};

Tasks.propTypes = {
  classes: PropTypes.object.isRequired,
  className: PropTypes.string,
  EventTracking: PropTypes.object.isRequired,
};

Tasks.defaultProps = {
  className: '',
};

const eventTrackingProperties = {
  nodeName: 'Tasks Section',
};
export default withEventTrackingLogEvent(
  eventTrackingProperties,
  withStyles(styles)(Tasks),
);
